import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiRefreshCw, FiX } from 'react-icons/fi';
import { ConfirmModal } from '../SharedModal';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { useMetaChannels } from '../metaConversation/useMetaChannels';
import { listSparkEntrypoints, type SparkEntrypointOption } from '../../services/SparkCampaignService';
import {
  createOfficialTemplate,
  deleteOfficialTemplate,
  getOfficialTemplate,
  listOfficialTemplates,
  updateOfficialTemplate,
  uploadOfficialTemplateMedia,
  type SparkOfficialTemplateDetail,
  type SparkOfficialTemplateSummary,
} from '../../services/SparkMessageTemplateService';
import {
  nextVariableIndex,
  placeholderIndexes,
  syncVariableDrafts,
  validateOfficialTemplate,
  type TemplateButtonDraft,
  type TemplateHeaderFormat,
  type TemplateVariableDraft,
} from '../../utils/sparkMessageTemplateRules';
import './SparkMessageTemplatesPanel.css';

type Props = {
  sectorId: number;
  onClose: () => void;
};

const LANGUAGES = [
  { value: 'pt_BR', label: 'Português (Brasil)' },
  { value: 'pt_PT', label: 'Português (Portugal)' },
  { value: 'en', label: 'Inglês' },
  { value: 'en_US', label: 'Inglês (EUA)' },
  { value: 'es', label: 'Espanhol' },
  { value: 'es_MX', label: 'Espanhol (México)' },
  { value: 'es_AR', label: 'Espanhol (Argentina)' },
];

const STATUS_LABEL: Record<string, string> = {
  APPROVED: 'Aprovado',
  PENDING: 'Em análise',
  REJECTED: 'Rejeitado',
  PAUSED: 'Pausado',
  DISABLED: 'Desativado',
  IN_APPEAL: 'Em recurso',
};

interface FormState {
  entrypointId: string;
  name: string;
  description: string;
  metaTemplateName: string;
  category: 'MARKETING' | 'UTILITY';
  language: string;
  headerFormat: TemplateHeaderFormat;
  headerText: string;
  mediaId: string;
  body: string;
  footer: string;
  buttons: TemplateButtonDraft[];
  variables: TemplateVariableDraft[];
}

const emptyForm = (): FormState => ({
  entrypointId: '',
  name: '',
  description: '',
  metaTemplateName: '',
  category: 'MARKETING',
  language: 'pt_BR',
  headerFormat: 'NONE',
  headerText: '',
  mediaId: '',
  body: '',
  footer: '',
  buttons: [],
  variables: [],
});

function statusClass(status?: string | null): string {
  const value = (status || '').toUpperCase();
  if (value === 'APPROVED') return 'smt-status approved';
  if (value === 'PENDING') return 'smt-status pending';
  if (value === 'REJECTED') return 'smt-status rejected';
  return 'smt-status';
}

function toMetaName(label: string): string {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 512);
}

function previewText(form: FormState): string {
  const sample = (index: number) => form.variables.find((v) => v.index === index)?.example || `{{${index}}}`;
  const fill = (text: string) => text.replace(/\{\{(\d+)\}\}/g, (_, n) => sample(Number(n)));
  const parts: string[] = [];
  if (form.headerFormat === 'TEXT' && form.headerText.trim()) parts.push(fill(form.headerText));
  else if (form.headerFormat !== 'NONE') parts.push(`[${form.headerFormat}]`);
  if (form.body.trim()) parts.push(fill(form.body));
  if (form.footer.trim()) parts.push(form.footer.trim());
  if (form.buttons.length) parts.push(form.buttons.map((b) => `[${b.text || 'Botão'}]`).join('  '));
  return parts.join('\n\n');
}

const SparkMessageTemplatesPanel: React.FC<Props> = ({ sectorId, onClose }) => {
  // Setor com WhatsApp direto na Meta: os modelos vêm da conta da Meta, não do Spark.
  const { whatsAppCloudActive } = useMetaChannels(sectorId);
  const [items, setItems] = useState<SparkOfficialTemplateSummary[]>([]);
  const [entrypoints, setEntrypoints] = useState<SparkEntrypointOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [thisNumberOnly, setThisNumberOnly] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [contentLocked, setContentLocked] = useState(false);
  const [rejectedReason, setRejectedReason] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const metaTouched = useRef(false);

  const showForm = creating || editingId != null;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templates, lines] = await Promise.all([
        listOfficialTemplates(sectorId),
        listSparkEntrypoints(sectorId),
      ]);
      setItems(templates);
      setEntrypoints(lines);
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível carregar os modelos de mensagem'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!creating || entrypoints.length !== 1) return;
    const only = entrypoints[0].entrypointId;
    setForm((prev) => (prev.entrypointId === only ? prev : { ...prev, entrypointId: only }));
  }, [creating, entrypoints]);

  const visible = useMemo(
    () => (thisNumberOnly ? items.filter((item) => item.boundToSector) : items),
    [items, thisNumberOnly],
  );

  const openCreate = () => {
    const next = emptyForm();
    if (entrypoints.length === 1) next.entrypointId = entrypoints[0].entrypointId;
    metaTouched.current = false;
    setForm(next);
    setEditingId(null);
    setCreating(true);
    setContentLocked(false);
    setRejectedReason(null);
    setError(null);
  };

  const openEdit = async (id: string) => {
    setError(null);
    setSaving(true);
    try {
      const detail = await getOfficialTemplate(sectorId, id);
      setForm(formFromDetail(detail));
      setEditingId(id);
      setCreating(false);
      setContentLocked(detail.contentEditable === false);
      setRejectedReason(detail.metaRejectedReason ?? null);
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível abrir o modelo'));
    } finally {
      setSaving(false);
    }
  };

  const syncFromText = (next: FormState): FormState => {
    const headerText = next.headerFormat === 'TEXT' ? next.headerText : '';
    const urls = next.buttons.filter((b) => b.type === 'URL').map((b) => b.url || '');
    const indexes = placeholderIndexes(headerText, next.body, ...urls);
    return { ...next, variables: syncVariableDrafts(indexes, next.variables) };
  };

  const patch = (partial: Partial<FormState>) => {
    setForm((prev) => syncFromText({ ...prev, ...partial }));
  };

  const insertVariable = (field: 'body' | 'headerText', textarea: HTMLTextAreaElement | null) => {
    setForm((prev) => {
      const source = field === 'body' ? prev.body : prev.headerText;
      const index = nextVariableIndex(`${prev.headerFormat === 'TEXT' ? prev.headerText : ''} ${prev.body} ${prev.buttons.map((b) => b.url || '').join(' ')}`);
      const token = `{{${index}}}`;
      const start = textarea?.selectionStart ?? source.length;
      const end = textarea?.selectionEnd ?? source.length;
      const value = `${source.slice(0, start)}${token}${source.slice(end)}`;
      return syncFromText({ ...prev, [field]: value });
    });
  };

  const save = async () => {
    const errors = contentLocked && !creating
      ? [
          ...(!form.name.trim() ? ['Informe o nome do modelo.'] : []),
          ...form.variables.flatMap((variable) => {
            if (!variable.title.trim()) return ['Informe um título para cada variável.'];
            if (variable.kind === 'lead' && variable.source !== 'chat.name') return ['Variáveis automáticas usam o nome do contato.'];
            return [];
          }),
        ]
      : validateOfficialTemplate({
      name: form.name,
      metaTemplateName: form.metaTemplateName,
      category: form.category,
      language: form.language,
      body: form.body,
      footer: form.footer,
      headerFormat: form.headerFormat,
      headerText: form.headerText,
      mediaId: form.mediaId,
      buttons: form.buttons,
      variables: form.variables.map((v) => ({
        ...v,
        source: v.kind === 'lead' ? 'chat.name' : null,
      })),
      entrypointId: form.entrypointId,
      editing: !creating,
    });
    if (errors.length) {
      setError(errors[0]);
      return;
    }
    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      whatsappEntrypointId: form.entrypointId || undefined,
      metaTemplateName: form.metaTemplateName.trim(),
      category: form.category,
      language: form.language,
      header: headerPayload(form),
      body: form.body,
      footer: form.footer.trim() || null,
      buttons: form.buttons.map((button) => ({
        type: button.type,
        text: button.text,
        url: button.type === 'URL' ? button.url : undefined,
        phoneNumber: button.type === 'PHONE_NUMBER' ? button.phoneNumber : undefined,
      })),
      variables: form.variables.map((variable) => ({
        index: variable.index,
        title: variable.title,
        example: variable.example,
        kind: variable.kind,
        source: variable.kind === 'lead' ? 'chat.name' : null,
      })),
    };
    try {
      if (creating) await createOfficialTemplate(sectorId, body);
      else if (editingId) await updateOfficialTemplate(sectorId, editingId, body);
      setCreating(false);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível salvar o modelo'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editingId) return;
    setSaving(true);
    setError(null);
    try {
      await deleteOfficialTemplate(sectorId, editingId);
      setConfirmDelete(false);
      setEditingId(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível excluir o modelo'));
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const mediaId = await uploadOfficialTemplateMedia(sectorId, file);
      patch({ mediaId });
    } catch (e) {
      setError(apiErrorMessage(e, 'Não foi possível enviar o arquivo de exemplo'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="smt-overlay" onClick={onClose}>
      <div className="smt-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="smt-title">
        <div className="smt-header">
          <h2 id="smt-title">{showForm ? (creating ? 'Novo modelo de mensagem' : 'Editar modelo de mensagem') : 'Modelos de mensagem'}</h2>
          <button type="button" className="smt-btn" onClick={onClose} aria-label="Fechar">
            <FiX size={16} />
          </button>
        </div>
        <div className="smt-body">
          {error && <div className="smt-error" style={{ marginBottom: 12 }}>{error}</div>}
          {!showForm ? (
            <>
              <div className="smt-toolbar">
                <button type="button" className="smt-btn-primary" onClick={openCreate}>
                  <FiPlus size={14} /> Novo modelo
                </button>
                <button type="button" className="smt-btn" onClick={() => void load()} disabled={loading}>
                  <FiRefreshCw size={14} /> Atualizar
                </button>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={thisNumberOnly} onChange={(e) => setThisNumberOnly(e.target.checked)} />
                  Somente este número
                </label>
              </div>
              <p className="smt-note">
                Os modelos oficiais são enviados à Meta pelo número de WhatsApp selecionado. Uma campanha só pode usar o modelo depois que ele for aprovado. Clique em Atualizar para ver o status mais recente.
              </p>
              {whatsAppCloudActive && (
                <p className="smt-note" role="note">
                  Esta conexão fala direto com a Meta. Os modelos da conexão anterior do WhatsApp não vêm para cá: crie de novo os que ainda usa e espere a aprovação da Meta (até 24 h).
                </p>
              )}
              {loading ? <p>Carregando…</p> : visible.length === 0 ? (
                <p className="smt-note" style={{ marginTop: 12 }}>
                  {thisNumberOnly
                    ? 'Nenhum modelo vinculado a esta conexão ainda. Crie um ou mostre todos os modelos oficiais da conta.'
                    : 'Nenhum modelo oficial encontrado.'}
                </p>
              ) : (
                <div className="smt-list" style={{ marginTop: 12 }}>
                  {visible.map((item) => (
                    <button key={item.id} type="button" className="smt-card" onClick={() => void openEdit(item.id)}>
                      <strong>{item.name}</strong>
                      <div className="smt-meta">
                        <span className={statusClass(item.metaTemplateStatus)}>
                          {STATUS_LABEL[(item.metaTemplateStatus || '').toUpperCase()] || item.metaTemplateStatus || 'Desconhecido'}
                        </span>
                        {item.metaTemplateName ? <span>{item.metaTemplateName}</span> : null}
                        {item.category ? <span>{item.category}</span> : null}
                        {item.language ? <span>{item.language}</span> : null}
                        {item.linePhone ? <span>{item.linePhone}</span> : <span>Número não registrado</span>}
                      </div>
                      {item.metaRejectedReason ? (
                        <div className="smt-meta">Rejeitado: {item.metaRejectedReason}</div>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <form className="smt-form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
              {creating ? (
                <p className="smt-note">Ao salvar, este modelo é enviado à Meta. Ele fica em análise e só pode ser usado em uma campanha depois de aprovado.</p>
              ) : contentLocked ? (
                <p className="smt-note">Este modelo ainda está em análise. Você pode alterar o nome e os títulos das variáveis, mas não o texto, os botões ou os exemplos.</p>
              ) : (
                <p className="smt-note">Alterar o conteúdo da mensagem envia o modelo de novo para a Meta. Ele sai da lista de campanhas até ser aprovado novamente.</p>
              )}
              {rejectedReason ? <div className="smt-error">A Meta rejeitou este modelo: {rejectedReason}</div> : null}

              {entrypoints.length > 1 && creating ? (
                <label>
                  Número
                  <select value={form.entrypointId} onChange={(e) => patch({ entrypointId: e.target.value })}>
                    <option value="">Selecione um número…</option>
                    {entrypoints.map((line) => (
                      <option key={line.entrypointId} value={line.entrypointId}>
                        {line.linePhone || line.label || line.entrypointId}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <p className="smt-meta">
                  Número: {entrypoints.find((line) => line.entrypointId === form.entrypointId)?.linePhone
                    || entrypoints[0]?.linePhone
                    || 'Número não registrado'}
                </p>
              )}
              {creating && entrypoints.length === 0 ? (
                <p className="smt-error">Ative esta conexão oficial antes de criar um modelo para o número dela.</p>
              ) : null}

              <div className="smt-grid">
                <label>
                  Nome
                  <input value={form.name} onChange={(e) => {
                    const name = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      name,
                      metaTemplateName: creating && !metaTouched.current ? toMetaName(name) : prev.metaTemplateName,
                    }));
                  }} />
                </label>
                <label>
                  Nome na Meta
                  <input
                    value={form.metaTemplateName}
                    disabled={!creating}
                    onChange={(e) => {
                      metaTouched.current = true;
                      patch({ metaTemplateName: e.target.value });
                    }}
                  />
                </label>
                <label>
                  Categoria
                  <select value={form.category} disabled={!creating} onChange={(e) => patch({ category: e.target.value as 'MARKETING' | 'UTILITY' })}>
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utilidade</option>
                  </select>
                </label>
                <label>
                  Idioma
                  <select value={form.language} disabled={!creating} onChange={(e) => patch({ language: e.target.value })}>
                    {LANGUAGES.map((language) => (
                      <option key={language.value} value={language.value}>{language.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label>
                Descrição
                <input value={form.description} onChange={(e) => patch({ description: e.target.value })} />
              </label>

              <label>
                Cabeçalho
                <select
                  value={form.headerFormat}
                  disabled={contentLocked}
                  onChange={(e) => patch({ headerFormat: e.target.value as TemplateHeaderFormat, headerText: '', mediaId: '' })}
                >
                  <option value="NONE">Nenhum</option>
                  <option value="TEXT">Texto</option>
                  <option value="IMAGE">Imagem</option>
                  <option value="VIDEO">Vídeo</option>
                  <option value="DOCUMENT">Documento</option>
                </select>
              </label>
              {form.headerFormat === 'TEXT' && (
                <label>
                  Texto do cabeçalho
                  <textarea
                    value={form.headerText}
                    disabled={contentLocked}
                    maxLength={60}
                    onChange={(e) => patch({ headerText: e.target.value })}
                  />
                  <span className="smt-counter">{form.headerText.trim().length}/60</span>
                  <button type="button" className="smt-btn" disabled={contentLocked} onClick={(e) => insertVariable('headerText', e.currentTarget.previousElementSibling?.previousElementSibling as HTMLTextAreaElement | null)}>
                    Inserir variável
                  </button>
                </label>
              )}
              {form.headerFormat !== 'NONE' && form.headerFormat !== 'TEXT' && (
                <label>
                  Arquivo de exemplo
                  <input
                    type="file"
                    disabled={contentLocked || uploading}
                    accept={form.headerFormat === 'IMAGE' ? 'image/jpeg,image/png' : form.headerFormat === 'VIDEO' ? 'video/mp4' : 'application/pdf'}
                    onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
                  />
                  <span className="smt-counter">{uploading ? 'Enviando…' : form.mediaId ? 'Arquivo de exemplo anexado' : 'JPEG, PNG, MP4 ou PDF de até 16 MB'}</span>
                </label>
              )}

              <label>
                Corpo
                <textarea
                  value={form.body}
                  disabled={contentLocked}
                  onChange={(e) => patch({ body: e.target.value })}
                />
                <span className="smt-counter">{form.body.trim().length}/1024</span>
                <button type="button" className="smt-btn" disabled={contentLocked} onClick={(e) => insertVariable('body', (e.currentTarget.previousElementSibling?.previousElementSibling as HTMLTextAreaElement | null))}>
                  Inserir variável
                </button>
              </label>
              <label>
                Rodapé
                <input value={form.footer} disabled={contentLocked} maxLength={60} onChange={(e) => patch({ footer: e.target.value })} />
                <span className="smt-counter">{form.footer.trim().length}/60</span>
              </label>

              <div className="smt-field">
                <div className="smt-row">
                  <span>Botões</span>
                  <button
                    type="button"
                    className="smt-btn"
                    disabled={contentLocked || form.buttons.length >= 10}
                    onClick={() => patch({ buttons: [...form.buttons, { type: 'QUICK_REPLY', text: '' }] })}
                  >
                    Adicionar botão
                  </button>
                </div>
                {form.buttons.map((button, index) => (
                  <div className="smt-button-row" key={`${button.type}-${index}`}>
                    <div className="smt-grid">
                      <select
                        value={button.type}
                        disabled={contentLocked}
                        onChange={(e) => {
                          const buttons = form.buttons.slice();
                          buttons[index] = { ...button, type: e.target.value as TemplateButtonDraft['type'] };
                          patch({ buttons });
                        }}
                      >
                        <option value="QUICK_REPLY">Resposta rápida</option>
                        <option value="URL">Link</option>
                        <option value="PHONE_NUMBER">Telefone</option>
                      </select>
                      <input
                        value={button.text}
                        disabled={contentLocked}
                        maxLength={25}
                        placeholder="Texto do botão"
                        onChange={(e) => {
                          const buttons = form.buttons.slice();
                          buttons[index] = { ...button, text: e.target.value };
                          patch({ buttons });
                        }}
                      />
                    </div>
                    {button.type === 'URL' && (
                      <input
                        value={button.url || ''}
                        disabled={contentLocked}
                        placeholder="https://seusite.com.br/{{1}}"
                        onChange={(e) => {
                          const buttons = form.buttons.slice();
                          buttons[index] = { ...button, url: e.target.value };
                          patch({ buttons });
                        }}
                      />
                    )}
                    {button.type === 'PHONE_NUMBER' && (
                      <input
                        value={button.phoneNumber || ''}
                        disabled={contentLocked}
                        placeholder="+5511999999999"
                        onChange={(e) => {
                          const buttons = form.buttons.slice();
                          buttons[index] = { ...button, phoneNumber: e.target.value };
                          patch({ buttons });
                        }}
                      />
                    )}
                    <button
                      type="button"
                      className="smt-btn"
                      disabled={contentLocked}
                      onClick={() => patch({ buttons: form.buttons.filter((_, i) => i !== index) })}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>

              {form.variables.length > 0 && (
                <div className="smt-field">
                  <span>Variáveis</span>
                  {form.variables.map((variable, index) => (
                    <div className="smt-var" key={variable.index}>
                      <strong>{`{{${variable.index}}}`}</strong>
                      <div className="smt-grid">
                        <input
                          value={variable.title}
                          placeholder="Título"
                          onChange={(e) => {
                            const variables = form.variables.slice();
                            variables[index] = { ...variable, title: e.target.value };
                            setForm((prev) => ({ ...prev, variables }));
                          }}
                        />
                        <input
                          value={variable.example}
                          placeholder="Exemplo"
                          disabled={contentLocked}
                          onChange={(e) => {
                            const variables = form.variables.slice();
                            variables[index] = { ...variable, example: e.target.value };
                            setForm((prev) => ({ ...prev, variables }));
                          }}
                        />
                        <select
                          value={variable.kind}
                          onChange={(e) => {
                            const kind = e.target.value as TemplateVariableDraft['kind'];
                            const variables = form.variables.slice();
                            variables[index] = { ...variable, kind, source: kind === 'lead' ? 'chat.name' : null };
                            setForm((prev) => ({ ...prev, variables }));
                          }}
                        >
                          <option value="common">Manual</option>
                          <option value="lead">Nome do contato</option>
                        </select>
                      </div>
                      <span className="smt-counter">{variable.example.trim().length}/15</span>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div className="smt-meta" style={{ marginBottom: 6 }}>Pré-visualização</div>
                <div className="smt-preview">{previewText(form) || 'A pré-visualização da mensagem aparece aqui.'}</div>
              </div>
            </form>
          )}
        </div>
        {showForm && (
          <div className="smt-footer">
            <div className="smt-row">
              {!creating && (
                <button type="button" className="smt-btn smt-btn-danger" disabled={saving} onClick={() => setConfirmDelete(true)}>
                  Excluir
                </button>
              )}
            </div>
            <div className="smt-row">
              <button type="button" className="smt-btn" onClick={() => { setCreating(false); setEditingId(null); setError(null); }}>
                Voltar
              </button>
              <button type="button" className="smt-btn-primary" disabled={saving || uploading} onClick={() => void save()}>
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
        <ConfirmModal
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={() => void remove()}
          title="Excluir modelo"
          message="Isso remove o modelo do Mends e, por ser oficial, também remove este idioma na Meta. O nome na Meta não pode ser reutilizado por cerca de 30 dias."
          confirmLabel="Excluir"
          cancelLabel="Manter"
          variant="danger"
          loading={saving}
          overlayClassName="smt-confirm-overlay"
        />
      </div>
    </div>
  );
};

function formFromDetail(detail: SparkOfficialTemplateDetail): FormState {
  const format = (detail.header?.format || 'NONE').toUpperCase() as TemplateHeaderFormat;
  const headerFormat: TemplateHeaderFormat = format === 'TEXT' || format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT' ? format : 'NONE';
  return {
    entrypointId: detail.entrypointId || '',
    name: detail.name || '',
    description: detail.description || '',
    metaTemplateName: detail.metaTemplateName || '',
    category: detail.category === 'UTILITY' ? 'UTILITY' : 'MARKETING',
    language: detail.language || 'pt_BR',
    headerFormat,
    headerText: detail.header?.text || '',
    mediaId: detail.header?.mediaId || '',
    body: detail.body || '',
    footer: detail.footer || '',
    buttons: (detail.buttons ?? []).map((button) => {
      const type = (button.type || 'QUICK_REPLY').toUpperCase();
      const normalized = type === 'URL' || type === 'PHONE_NUMBER' ? type : 'QUICK_REPLY';
      return {
        type: normalized,
        text: button.text,
        url: button.url,
        phoneNumber: button.phoneNumber,
      };
    }),
    variables: detail.variables ?? [],
  };
}

function headerPayload(form: FormState): { format: string; text?: string; mediaId?: string } | null {
  if (form.headerFormat === 'NONE') return null;
  if (form.headerFormat === 'TEXT') return { format: 'TEXT', text: form.headerText };
  return { format: form.headerFormat, mediaId: form.mediaId };
}

export default SparkMessageTemplatesPanel;
